// PTE Core speaking screens, faithful to Pearson per-task mic behavior
// (memory/EXAM_INTERFACE_SPEC.md): Read Aloud preps then TONES; Repeat
// Sentence opens the mic IMMEDIATELY after audio with NO tone; Answer Short
// Question opens immediately and auto-completes after a 3-second pause.
// Recording happens exactly once, on the exam's clock.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Mic, Volume2 } from 'lucide-react';

import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import DiagramCard from '@/components/DiagramCard';
import { acquireMicStream, buildLevelMeter, releaseStream as releaseMicStream } from '@/lib/micRecorder';
import { cn } from '@/lib/utils';

// `submit()` always navigates to the review page after uploading. The
// PHASE.DONE block that used to display feedback inline is unreachable and
// was deleted (its `result`/`marking` state + `AssignmentNextCTA`/gate-flag
// imports followed it into the bin) so the runner reads for what it does.
const PHASE = { INTRO: 'INTRO', AUDIO: 'AUDIO', PREP: 'PREP', RECORD: 'RECORD', SUBMITTING: 'SUBMITTING', ERROR: 'ERROR' };

// MediaRecorder mimeType pick that survives Safari (no audio/webm support).
function pickAudioMime() {
  const MR = typeof window !== 'undefined' ? window.MediaRecorder : null;
  const candidates = [
    { mime: 'audio/webm;codecs=opus', ext: 'webm' },
    { mime: 'audio/webm', ext: 'webm' },
    { mime: 'audio/mp4', ext: 'm4a' },
    { mime: 'audio/aac', ext: 'aac' },
  ];
  if (MR && typeof MR.isTypeSupported === 'function') {
    for (const c of candidates) {
      if (MR.isTypeSupported(c.mime)) return c;
    }
  }
  return { mime: '', ext: 'webm' };
}

const TASK_META = {
  pte_read_aloud: {
    title: 'Read Aloud',
    flow: ['Text appears with a countdown. Read it silently while the countdown runs.', 'A short tone plays, then the status box shows Recording.', 'Read the text aloud naturally. Recording stops on its own.'],
  },
  pte_repeat_sentence: {
    title: 'Repeat Sentence',
    flow: ['You hear a sentence exactly once.', 'The microphone opens IMMEDIATELY when the audio ends. There is NO tone on this task.', 'Repeat the sentence exactly as you heard it.'],
  },
  pte_describe_image: {
    title: 'Describe Image',
    flow: ['An image appears with 25 seconds to study it.', 'A short tone plays, then recording starts.', 'Describe what the image shows for up to 40 seconds.'],
  },
  pte_respond_situation: {
    title: 'Respond to a Situation',
    flow: ['You hear and read a description of a situation.', 'You have 20 seconds to think, then a tone plays.', 'Give your response in up to 40 seconds.'],
  },
  pte_asq: {
    title: 'Answer Short Question',
    flow: ['You hear a short question. The microphone opens immediately, with no tone.', 'Answer in one or a few words. Long answers are not better answers.', 'On the real exam the recording completes itself if you pause for 3 seconds. The same happens here.'],
  },
};

function playTone(onDone) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.value = 0.15;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    setTimeout(() => { osc.stop(); ctx.close(); onDone(); }, 350);
  } catch {
    onDone();
  }
}

export default function PTESpeakingRunner() {
  const { taskType } = useParams();
  const [searchParams] = useSearchParams();
  const setParam = searchParams.get('set');
  const exam = 'pte';
  const navigate = useNavigate();
  const meta = TASK_META[taskType] || { title: 'Speaking task', flow: [] };

  const [item, setItem] = useState(null);
  const [phase, setPhase] = useState(PHASE.INTRO);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [micError, setMicError] = useState('');
  const [micBusy, setMicBusy] = useState(false);
  const [level, setLevel] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedRef = useRef(null);
  const phaseRef = useRef(PHASE.INTRO);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const mimeRef = useRef({ mime: '', ext: 'webm' });
  const meterRef = useRef(null);
  const levelRafRef = useRef(0);
  const asqSilenceRafRef = useRef(0);

  const setPhaseBoth = (p) => { setPhase(p); phaseRef.current = p; };

  const load = useCallback(async () => {
    setError('');
    setPhaseBoth(PHASE.INTRO);
    try {
      const res = await api.practiceSets(exam, taskType, 1, setParam);
      if (res.data.review_submission_id) {
        navigate(`/${exam}/submissions/${res.data.review_submission_id}/review`, { replace: true });
        return;
      }
      setItem(res.data.sets[0]);
    } catch (err) {
      setError(err.response?.data?.detail || 'No prompt available for this task yet.');
      setPhaseBoth(PHASE.ERROR);
    }
  }, [taskType, setParam, exam, navigate]);

  useEffect(() => { load(); }, [load]);

  const submit = useCallback(async (blob, durationSec, currentItem, ext, clientSilent) => {
    setPhaseBoth(PHASE.SUBMITTING);
    try {
      const form = new FormData();
      form.append('set_id', currentItem.set_id);
      form.append('duration_sec', String(durationSec));
      form.append('audio', blob, `response.${ext}`);
      if (clientSilent) form.append('client_silent', 'true');
      const res = await api.submitSpeaking(exam, form);
      // Backend persists + kicks off background grading in ~200ms; review
      // page polls for the final mark. No blocking wait on the LLM.
      const s = res.data.submission;
      navigate(`/${exam}/submissions/${s.submission_id}/review`, { replace: true });
    } catch (err) {
      // Race-condition safety: if a second submit landed after the mount
      // check (rapid double-tap, two tabs), the backend returns 409 with the
      // existing submission_id. Route the student to the review page.
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.submission_id) {
        navigate(`/${exam}/submissions/${detail.submission_id}/review`, { replace: true });
        return;
      }
      setError(typeof detail === 'string' ? detail : (detail?.message || 'Submission failed.'));
      setPhaseBoth(PHASE.ERROR);
    }
  }, [exam, navigate]);

  const stopRecording = useCallback(() => {
    if (asqSilenceRafRef.current) { cancelAnimationFrame(asqSilenceRafRef.current); asqSilenceRafRef.current = 0; }
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
  }, []);

  const releaseStream = useCallback(() => {
    if (meterRef.current) { meterRef.current.stop(); meterRef.current = null; }
    if (levelRafRef.current) { cancelAnimationFrame(levelRafRef.current); levelRafRef.current = 0; }
    if (asqSilenceRafRef.current) { cancelAnimationFrame(asqSilenceRafRef.current); asqSilenceRafRef.current = 0; }
    const stream = streamRef.current;
    if (stream) {
      releaseMicStream(stream);
      streamRef.current = null;
    }
  }, []);

  // Acquire the mic on the START click, before any countdown or audio plays.
  // This mirrors the real exam's mic check at the front of the section so the
  // browser permission prompt never interrupts a running clock.
  const acquireMic = useCallback(async () => {
    setMicError('');
    if (streamRef.current) return true;
    setMicBusy(true);
    try {
      const { stream, mime } = await acquireMicStream();
      streamRef.current = stream;
      mimeRef.current = mime;
      setMicBusy(false);
      return true;
    } catch (err) {
      setMicBusy(false);
      setMicError(err.message || 'Could not access the microphone.');
      return false;
    }
  }, []);

  // ASQ only: the real exam completes the recording after a 3s pause AFTER
  // speech has started. Uses the shared meter's quietMs() so we don't spin up
  // a second AudioContext.
  const watchSilenceForAsq = useCallback(() => {
    const tick = () => {
      const m = meterRef.current;
      if (m && m.didSpeak() && m.quietMs() > 3000) { stopRecording(); return; }
      asqSilenceRafRef.current = requestAnimationFrame(tick);
    };
    asqSilenceRafRef.current = requestAnimationFrame(tick);
  }, [stopRecording]);

  const startRecording = useCallback((currentItem) => {
    const stream = streamRef.current;
    if (!stream) {
      setMicError('The microphone was disconnected. Click Start again to retry.');
      setPhaseBoth(PHASE.INTRO);
      return;
    }
    try {
      const { mime, ext } = mimeRef.current;
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      startedRef.current = Date.now();
      meterRef.current = buildLevelMeter(stream);
      const pollLevel = () => {
        setLevel(meterRef.current?.getLevel() || 0);
        levelRafRef.current = requestAnimationFrame(pollLevel);
      };
      levelRafRef.current = requestAnimationFrame(pollLevel);
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, rec.mimeType ? { type: rec.mimeType } : {});
        const durationSec = (Date.now() - startedRef.current) / 1000;
        const clientSilent = !(meterRef.current?.didSpeak());
        releaseStream();
        submit(blob, durationSec, currentItem, ext, clientSilent);
      };
      rec.start();
      setPhaseBoth(PHASE.RECORD);
      setCountdown(currentItem.response_spec?.record_seconds || 40);
      if (taskType === 'pte_asq') watchSilenceForAsq();
    } catch {
      releaseStream();
      setMicError('Could not start the recorder. Reload the page and try again.');
      setPhaseBoth(PHASE.INTRO);
    }
  }, [submit, taskType, watchSilenceForAsq, releaseStream]);

  const openMic = useCallback((currentItem) => {
    const spec = currentItem.response_spec || {};
    if (spec.tone) playTone(() => startRecording(currentItem));
    else startRecording(currentItem); // RS and ASQ: no tone, mic opens cold
  }, [startRecording]);

  const afterAudio = useCallback((currentItem) => {
    const spec = currentItem.response_spec || {};
    if ((spec.prep_seconds || 0) > 0) {
      setCountdown(spec.prep_seconds);
      setPhaseBoth(PHASE.PREP);
    } else {
      openMic(currentItem);
    }
  }, [openMic]);

  const begin = useCallback(async (currentItem) => {
    const stim = currentItem.stimulus || {};
    if (stim.audio_ref) {
      setPhaseBoth(PHASE.AUDIO);
      try {
        const res = await api.contentAudioBlob(stim.audio_ref);
        const url = URL.createObjectURL(res.data);
        const a = new Audio(url);
        audioRef.current = a;
        a.onended = () => { URL.revokeObjectURL(url); afterAudio(currentItem); };
        await a.play();
      } catch {
        // No rendered audio yet: fall through on the same clock so the task
        // still trains (transcript shows in the stimulus card below).
        afterAudio(currentItem);
      }
    } else {
      afterAudio(currentItem);
    }
  }, [afterAudio]);

  useEffect(() => {
    if (phase !== PHASE.PREP && phase !== PHASE.RECORD) return undefined;
    const interval = setInterval(() => {
      setCountdown((s) => {
        if (s <= 1) {
          clearInterval(interval);
          // Mutually exclusive branches — openMic transitions to RECORD
          // synchronously (via startRecording), so we MUST snapshot the
          // pre-transition phase or the second branch would fire in
          // the same tick and stop the recording immediately.
          const before = phaseRef.current;
          if (before === PHASE.PREP) openMic(item);
          else if (before === PHASE.RECORD) stopRecording();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase, item, openMic, stopRecording]);

  useEffect(() => () => { // unmount: release mic + audio
    stopRecording();
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioRef.current) audioRef.current.pause();
  }, [stopRecording]);

  const spec = item?.response_spec || {};
  const stim = item?.stimulus || {};
  const showTranscriptFallback = (phase === PHASE.PREP || phase === PHASE.RECORD) && stim.transcript && !stim.audio_ref;

  const runStart = useCallback(async () => {
    const ok = await acquireMic();
    if (!ok) return;
    begin(item);
  }, [acquireMic, begin, item]);

  if (phase === PHASE.ERROR) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-100">
        <p className="max-w-md text-center text-slate-600">{error}</p>
        <Link to="/pte/practice" className="text-sm font-medium text-blue-800 hover:underline">Back to practice</Link>
      </div>
    );
  }
  if (!item) return <div className="flex min-h-screen items-center justify-center bg-slate-100 text-slate-500">Preparing…</div>;

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-blue-900 text-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <Mic className="h-4 w-4 opacity-80" />
            <div>
              <p className="text-[11px] uppercase tracking-wider text-blue-200">PTE CORE practice • Speaking</p>
              <p className="text-sm font-semibold">{meta.title}</p>
            </div>
          </div>
          {(phase === PHASE.PREP || phase === PHASE.RECORD) && (
            <span
              className={cn('rounded px-2.5 py-1 text-sm font-semibold tabular-nums', phase === PHASE.PREP ? 'bg-blue-950/60' : 'bg-red-600')}
              data-testid="pte-speaking-countdown"
            >
              {phase === PHASE.PREP ? 'Beginning in: ' : 'Recording: '}{countdown}s
            </span>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-10">
        {phase === PHASE.INTRO && (
          <div className="border border-slate-300 bg-white p-10 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-blue-900">Instructions</p>
            <h1 className="mt-2 text-xl font-semibold text-slate-900">{meta.title}</h1>
            {spec.instruction && <p className="mt-3 text-[15px] leading-7 text-slate-600">{spec.instruction}</p>}
            <ul className="mt-6 space-y-3 text-[15px] leading-6 text-slate-700">
              {meta.flow.map((line, i) => (
                <li key={i} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-900" />{line}</li>
              ))}
            </ul>
            <div className="mt-10 flex items-center justify-between">
              <Link to="/pte/practice" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="pte-speaking-exit">
                <ArrowLeft className="h-4 w-4" /> Exit
              </Link>
              <Button
                size="lg"
                className="bg-blue-900 px-10 hover:bg-blue-800 disabled:opacity-60"
                data-testid="pte-speaking-start"
                disabled={micBusy}
                onClick={runStart}
              >
                {micBusy ? 'Checking mic…' : 'START'}
              </Button>
            </div>
            {micError && (
              <div
                className="mt-6 border-l-2 border-red-500 bg-red-50/60 px-4 py-3 text-sm text-red-700"
                data-testid="pte-speaking-mic-error"
              >
                <p>{micError}</p>
                <p className="mt-1 text-xs text-red-600/80">
                  If the prompt does not appear, click the lock or camera icon in your browser&apos;s address bar, allow the microphone, then try again.
                </p>
                <button
                  type="button"
                  data-testid="pte-speaking-mic-retry"
                  disabled={micBusy}
                  onClick={runStart}
                  className="mt-3 inline-flex items-center rounded bg-blue-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
                >
                  {micBusy ? 'Checking mic…' : 'Try again'}
                </button>
              </div>
            )}
          </div>
        )}

        {phase === PHASE.AUDIO && (
          <div className="flex items-center justify-center gap-3 border border-slate-300 bg-white px-6 py-16 text-sm font-semibold text-slate-600 shadow-sm" data-testid="pte-speaking-audiobox">
            <Volume2 className="h-5 w-5 animate-pulse text-blue-900" />
            Audio is playing. It plays only once.
          </div>
        )}

        {(phase === PHASE.PREP || phase === PHASE.RECORD) && (
          <div className="space-y-6">
            {(stim.text || stim.diagram || showTranscriptFallback) && (
              <div className="border border-slate-300 bg-white shadow-sm">
                <div className="border-b border-slate-200 bg-slate-50 px-5 py-2.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {taskType === 'pte_read_aloud' ? 'Read this text aloud' : 'Task'}
                  </p>
                </div>
                <div className="px-6 py-5">
                  {stim.diagram && <DiagramCard diagram={stim.diagram} />}
                  {stim.text && <p className="text-[17px] leading-8 text-slate-800" data-testid="pte-speaking-text">{stim.text}</p>}
                  {showTranscriptFallback && (
                    <p className="mt-3 border-l-2 border-amber-400 bg-amber-50/50 px-4 py-3 text-sm leading-6 text-slate-700">
                      Audio for this prompt is still rendering. Read the line below, cover it, then respond:
                      <span className="mt-1 block font-medium text-slate-900">{stim.transcript}</span>
                    </p>
                  )}
                </div>
              </div>
            )}
            <div
              className={cn(
                'flex items-center justify-center gap-3 border px-6 py-5 text-sm font-semibold',
                phase === PHASE.PREP ? 'border-slate-300 bg-white text-slate-600' : 'border-red-200 bg-red-50 text-red-700'
              )}
              data-testid="pte-speaking-statusbox"
            >
              <span className={cn('h-2.5 w-2.5 rounded-full', phase === PHASE.PREP ? 'bg-slate-400' : 'animate-pulse bg-red-600')} />
              {phase === PHASE.PREP
                ? (spec.tone ? 'Prepare. A short tone plays when recording begins.' : 'Prepare. Recording begins when the countdown ends.')
                : 'Recording. Speak now.'}
            </div>
            {phase === PHASE.RECORD && (
              <div className="border border-slate-200 bg-white px-5 py-4" data-testid="pte-speaking-mic-meter">
                <div className="flex items-center gap-3">
                  <Mic className="h-4 w-4 text-slate-500" />
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mic input</p>
                  <p className="ml-auto text-xs text-slate-400">
                    {level > 0.035 ? 'Picking up your voice' : 'No sound detected — speak up'}
                  </p>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={cn('h-full transition-[width] duration-75', level > 0.035 ? 'bg-emerald-500' : 'bg-slate-300')}
                    style={{ width: `${Math.min(100, Math.round(level * 800))}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {phase === PHASE.SUBMITTING && (
          <div className="flex flex-col items-center gap-3 py-24 text-slate-500">
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-blue-900" />
            Uploading and transcribing your response…
          </div>
        )}
      </main>
    </div>
  );
}
